import React, { memo } from 'react';

import { StudioVisualizationType } from '@blockworks/platform/api/research/studio';
import { Text } from '@blockworks/ui';
import { Bit } from '@blockworks/ui/factory';

import type { PreloadedDashboardData } from '../../../dashboard-grid/dashboard-grid.model';
import { useIsVisible } from '../../utils/use-is-visible';
import { VisualizationRenderer } from '../visualization-renderer';
import type { VisualizationRendererProps } from '../visualization-renderer/visualization-renderer.model';

type PreloadedWithExecutionVisualizationProps = {
    visualization?: PreloadedDashboardData[number];
} & Pick<VisualizationRendererProps, 'componentSlots'>;

interface PreloadedWithExecutionVisualizationComponent
    extends React.NamedExoticComponent<PreloadedWithExecutionVisualizationProps> {}

const PreloadedWithExecutionVisualization = memo(
    function PreloadedWithExecutionVisualization({
        visualization,
        componentSlots,
    }: PreloadedWithExecutionVisualizationProps) {
        const { containerRef, isViewable, wasViewable } = useIsVisible(true);

        let content: React.ReactNode = null;
        const isHidden = visualization?.type === StudioVisualizationType.Table ? !wasViewable : !isViewable;

        if (!isHidden) {
            if (!visualization?.execution) {
                console.error('Visualization or Execution data not provided.', {
                    hasVisualization: !!visualization,
                    hasExecution: !!visualization?.execution,
                });

                content = (
                    <Bit.div display="flex" justifyContent="center" alignItems="center" w="full" h="full">
                        <Text center>Visualization not found.</Text>
                    </Bit.div>
                );
            } else {
                content = (
                    <VisualizationRenderer
                        componentSlots={componentSlots}
                        visualization={{ ...visualization, execution: visualization?.execution }}
                    />
                );
            }
        }

        return (
            <Bit.div
                ref={containerRef}
                w="full"
                h="full"
                justifyContent="center"
                alignItems="center"
                pos="relative"
                className="scroller"
            >
                {content}
            </Bit.div>
        );
    },
    (prev, next) => prev.visualization?.id === next.visualization?.id,
) as PreloadedWithExecutionVisualizationComponent;

export { PreloadedWithExecutionVisualization };
